export const serializeTabSeparatedClipboard = (
  rows: readonly (readonly string[])[],
) =>
  rows
    .map((row, index) => {
      // Keep a final empty cell distinct from an optional row terminator.
      if (
        rows.length > 1 &&
        index === rows.length - 1 &&
        row.length === 1 &&
        row[0] === ""
      )
        return '""';
      return row
        .map((value) =>
          /[\t\n"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value,
        )
        .join("\t");
    })
    .join("\n");

// Excel writes clipboard text as tab-separated rows and applies CSV quoting to
// cells that contain tabs, line breaks, or quotes. Parse that text directly so
// an empty source cell still occupies its column even when a browser grid drops
// the matching empty HTML cell.
export const readTabSeparatedClipboardRows = function* (
  text: string,
): Generator<string[]> {
  let row: string[] = [];
  let value = "";
  let inQuotes = false;
  let endedWithRowBreak = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (character === '"') {
      if (inQuotes && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else if (inQuotes) {
        inQuotes = false;
      } else if (value === "") {
        inQuotes = true;
      } else {
        value += character;
      }
      endedWithRowBreak = false;
      continue;
    }

    if (!inQuotes && character === "\t") {
      row.push(value);
      value = "";
      endedWithRowBreak = false;
      continue;
    }

    if (!inQuotes && (character === "\n" || character === "\r")) {
      row.push(value);
      yield row;
      row = [];
      value = "";
      endedWithRowBreak = true;
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      continue;
    }

    value += character;
    endedWithRowBreak = false;
  }

  if (!endedWithRowBreak || row.length > 0 || value !== "") {
    row.push(value);
    yield row;
  }
};

export const parseTabSeparatedClipboard = (text: string): string[][] => {
  const rows = Array.from(readTabSeparatedClipboardRows(text));
  const columnCount = rows.reduce(
    (count, parsedRow) => Math.max(count, parsedRow.length),
    0,
  );
  return rows.map((parsedRow) => [
    ...parsedRow,
    ...Array<string>(columnCount - parsedRow.length).fill(""),
  ]);
};
